import React from 'react';
import { Button } from '@ai-content-hub/content-kit';
import { Check, Contrast, Info, TriangleAlert, X, type LucideIcon } from 'lucide-react';
import { cn } from '../lib/utils';

export type FeedbackType = 'success' | 'error' | 'warning' | 'info' | 'partial';

export interface FeedbackDisplayProps {
  type: FeedbackType;
  message?: string;
  title?: string;
  children?: React.ReactNode;
  className?: string;
  showIcon?: boolean;
  dismissible?: boolean;
  onDismiss?: () => void;
  /** Translated aria-label for dismiss button */
  dismissAriaLabel?: string;
}

// Icons are SVG (lucide) rather than text glyphs: '✕' / '◐' have no glyph in the iOS system
// font stack and rendered as a tofu "[?]" box in the mobile WebView. The icon chip uses an
// explicit tinted background (Tailwind 3 does not apply bg-opacity to bg-current, which
// painted a solid disc over the glyph).
const feedbackConfig: Record<
  FeedbackType,
  { Icon: LucideIcon; bgClass: string; iconBgClass: string; textClass: string; borderClass: string }
> = {
  success: {
    Icon: Check,
    bgClass: 'bg-success/10',
    iconBgClass: 'bg-success/15',
    textClass: 'text-success',
    borderClass: 'border-success/30',
  },
  error: {
    Icon: X,
    bgClass: 'bg-destructive/10',
    iconBgClass: 'bg-destructive/15',
    textClass: 'text-destructive',
    borderClass: 'border-destructive/30',
  },
  warning: {
    Icon: TriangleAlert,
    bgClass: 'bg-warning/10',
    iconBgClass: 'bg-warning/15',
    textClass: 'text-warning',
    borderClass: 'border-warning/30',
  },
  info: {
    Icon: Info,
    bgClass: 'bg-primary/10',
    iconBgClass: 'bg-primary/15',
    textClass: 'text-primary',
    borderClass: 'border-primary/30',
  },
  partial: {
    Icon: Contrast,
    bgClass: 'bg-warning/10',
    iconBgClass: 'bg-warning/15',
    textClass: 'text-warning',
    borderClass: 'border-warning/30',
  },
};

/**
 * FeedbackDisplay - Shows feedback messages with appropriate styling
 */
export function FeedbackDisplay({
  type,
  message,
  title,
  children,
  className,
  showIcon = true,
  dismissible = false,
  onDismiss,
  dismissAriaLabel,
}: FeedbackDisplayProps): React.ReactElement {
  const config = feedbackConfig[type];

  return (
    // EAA-1679: data-slot="card" stamped directly on the div (not the kit Card
    // component) — this alert already carries its own tinted border/background;
    // wrapping it in Card would add a second border on top, stacking visibly.
    <div
      data-slot="card"
      className={cn(
        'feedback-display',
        'flex items-start gap-3 p-4 rounded-lg border',
        config.bgClass,
        config.textClass,
        config.borderClass,
        className
      )}
      role="alert"
    >
      {showIcon && (
        <span
          className={cn(
            'feedback-display-icon flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full',
            config.iconBgClass
          )}
          aria-hidden
        >
          <config.Icon className="size-3" strokeWidth={3} />
        </span>
      )}
      
      <div className="flex-1 min-w-0">
        {title && (
          <h4 className="font-semibold mb-1">{title}</h4>
        )}
        {message && (
          <p className="text-sm">{message}</p>
        )}
        {children}
      </div>

      {dismissible && onDismiss && (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          onClick={onDismiss}
          className="flex-shrink-0 hover:bg-foreground/10"
          aria-label={dismissAriaLabel ?? 'Dismiss'}
        >
          <X className="size-4" />
        </Button>
      )}
    </div>
  );
}

/**
 * Determine feedback type from correctness
 */
export function getFeedbackType(
  isCorrect: boolean | null,
  isPartial?: boolean
): FeedbackType {
  if (isCorrect === null) return 'info';
  if (isPartial) return 'partial';
  return isCorrect ? 'success' : 'error';
}
